import { useRef, useState } from 'react'
import { ImagePlus, Loader2, Star, Trash2, Upload } from 'lucide-react'
import { thumbUrl } from '@najah/core'
import { Alert, Button, cn, useToast } from '@najah/ui'
import { uploadsApi } from '../lib/uploadsApi'

interface Props {
  value: string[]
  onChange: (images: string[]) => void
  max?: number
  disabled?: boolean
}

/**
 * صور المنتج. الترتيب مقصود: الأولى هي التي يراها الزبون في بطاقة المتجر،
 * فالترقية إلى الأولى إجراء صريح لا إعادة ترتيب بالسحب.
 *
 * الرفع يحدث فوراً على الخادم، والروابط تُحفظ مع المنتج.
 * صورة رُفعت لمنتج لم يُحفظ تبقى يتيمة في التخزين — وهي أهون من رفعٍ يفشل
 * بعد أن ملأ المستخدم النموذج كلّه.
 */
export function ImageUploader({ value, onChange, max = 6, disabled }: Props) {
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  const room = max - value.length

  async function upload(files: FileList | File[]) {
    setError(null)
    const list = [...files].slice(0, room)
    if (list.length === 0) return

    setBusy((n) => n + list.length)
    const added: string[] = []
    for (const file of list) {
      try {
        const { url } = await uploadsApi.productImage(file)
        added.push(url)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'تعذّر رفع الصورة')
      } finally {
        setBusy((n) => n - 1)
      }
    }
    if (added.length) onChange([...value, ...added])
  }

  async function remove(url: string) {
    onChange(value.filter((u) => u !== url))
    try {
      await uploadsApi.removeProductImage(url)
    } catch {
      // الصورة خرجت من المنتج؛ بقاؤها في التخزين لا يضرّ المستخدم
      toast.success('حُذفت من المنتج', 'بقيت نسخة في التخزين — لا أثر لها على المتجر')
    }
  }

  function promote(url: string) {
    onChange([url, ...value.filter((u) => u !== url)])
  }

  return (
    <div className="space-y-3">
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="flex flex-wrap gap-3">
        {value.map((url, i) => (
          <div
            key={url}
            className={cn(
              'group relative size-24 overflow-hidden rounded-lg border bg-bg-subtle',
              i === 0 ? 'border-primary' : 'border-border',
            )}
          >
            <img src={thumbUrl(url) ?? url} alt="" className="size-full object-cover" loading="lazy" />

            {i === 0 && (
              <span className="absolute inset-x-0 top-0 bg-primary/90 py-0.5 text-center text-[10px] font-medium text-primary-fg">
                الرئيسية
              </span>
            )}

            {!disabled && (
              <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/55 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                {i !== 0 && (
                  <button
                    type="button"
                    onClick={() => promote(url)}
                    title="اجعلها الرئيسية"
                    className="grid size-8 place-items-center rounded-md bg-white/15 text-white hover:bg-white/30"
                  >
                    <Star className="size-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void remove(url)}
                  title="حذف"
                  className="grid size-8 place-items-center rounded-md bg-white/15 text-white hover:bg-danger"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            )}
          </div>
        ))}

        {busy > 0 && Array.from({ length: busy }).map((_, i) => (
          <div key={`busy-${i}`} className="grid size-24 place-items-center rounded-lg border border-border bg-bg-subtle">
            <Loader2 className="size-5 animate-spin text-fg-subtle" />
          </div>
        ))}

        {!disabled && room > 0 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); void upload(e.dataTransfer.files) }}
            className={cn(
              'grid size-24 place-items-center rounded-lg border-2 border-dashed text-fg-subtle transition-colors',
              dragging ? 'border-primary bg-primary-subtle text-primary' : 'border-border hover:border-primary hover:text-primary',
            )}
          >
            <span className="flex flex-col items-center gap-1">
              {dragging ? <Upload className="size-5" /> : <ImagePlus className="size-5" />}
              <span className="text-[10px]">أضف صورة</span>
            </span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) void upload(e.target.files)
          e.target.value = ''
        }}
      />

      <p className="text-xs text-fg-subtle">
        JPEG أو PNG أو WebP، حتى ٨ ميغابايت — تُصغَّر تلقائياً إلى webp.
        الصورة الأولى هي التي يراها الزبون في المتجر.
      </p>

      {value.length === 0 && busy === 0 && (
        <Button
          type="button" variant="secondary" size="sm" disabled={disabled}
          startIcon={<ImagePlus className="size-4" />}
          onClick={() => inputRef.current?.click()}
        >
          اختر صوراً
        </Button>
      )}
    </div>
  )
}
